/**
 * A tenant the person NAMED before signing in — by its link (`/t/<tenant id>`) or the workspace-ID
 * box on `/login/workspace` (tenant-scoped sign-in, 2026-10-10).
 *
 * The Entra provider sends an unauthenticated browser to Microsoft before any route renders, and
 * Microsoft returns it to the app's origin, so the name has to be written down FIRST. It is kept in
 * sessionStorage for the round trip and taken exactly once by the app, which then asks the API to
 * record the entry (POST /v1/session/tenant-entry). The value is only a selector: nothing is granted
 * by holding it, and the server checks membership.
 *
 * Shared by the generated auth provider/gate (auth-ui-generator) and the platform app, so the key
 * and the link shape have one definition. sessionStorage can throw (private mode, blocked site
 * data); every access is guarded and a failure only means the name is not remembered.
 */

const PENDING_TENANT_ENTRY_KEY = 'inferrex.pendingTenantEntry';

/** The tenant-link path prefix: `/t/<tenant id>`. */
export const TENANT_LINK_PREFIX = '/t/';

/** The sign-in page with the workspace-ID box. */
export const WORKSPACE_SIGN_IN_PATH = '/login/workspace';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface SessionStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function store(): SessionStore | undefined {
  try {
    return (globalThis as { sessionStorage?: SessionStore }).sessionStorage;
  } catch {
    return undefined;
  }
}

/** Is this shaped like a tenant id? (Shape only — the server decides everything else.) */
export function looksLikeTenantId(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/** The shareable sign-in link for a tenant. */
export function tenantSignInLink(origin: string, tenantId: string): string {
  return `${origin.replace(/\/$/, '')}${TENANT_LINK_PREFIX}${tenantId}`;
}

/** Remember a named tenant for after sign-in. Anything not shaped like a tenant id is ignored. */
export function setPendingTenantEntry(tenantId: string): void {
  const id = tenantId.trim().toLowerCase();
  if (!looksLikeTenantId(id)) return;
  try { store()?.setItem(PENDING_TENANT_ENTRY_KEY, id); } catch { /* not remembered */ }
}

/**
 * If `pathname` is a tenant link, remember its tenant and return true. The caller then moves the
 * browser off the link (the app has no `/t/*` page) — before or after sign-in alike.
 */
export function captureTenantLink(pathname: string): boolean {
  if (!pathname.startsWith(TENANT_LINK_PREFIX)) return false;
  const id = decodeURIComponent(pathname.slice(TENANT_LINK_PREFIX.length).split('/')[0] ?? '');
  // A malformed link is still "a link": it leaves the path too, and the app shows the same
  // generic "can't sign in to that workspace" it shows for any other refused name.
  try { store()?.setItem(PENDING_TENANT_ENTRY_KEY, looksLikeTenantId(id) ? id.toLowerCase() : 'invalid'); } catch { /* not remembered */ }
  return true;
}

/** Take (read and clear) the named tenant, if any. 'invalid' marks a malformed link. */
export function takePendingTenantEntry(): string | null {
  const s = store();
  if (!s) return null;
  try {
    const value = s.getItem(PENDING_TENANT_ENTRY_KEY);
    s.removeItem(PENDING_TENANT_ENTRY_KEY);
    return value;
  } catch {
    return null;
  }
}
